import { differenceInCalendarDays, isValid, parseISO } from 'date-fns';
import type { Entitlement } from '@/@types/entitlement';
import { EntitlementType } from '@/@types/entitlement';
import { DEPOSIT_SERVICE_STATUS } from '@/@types/deposit';
import type { DepositState } from '@/@types/deposit';
import { BASE_PATH, MIETER_PLUS_PATH, WOHNEN_PLUS_PATH } from '@/domain/routes';
import type { DepositGuaranteePromoTab } from './DepositGuaranteeBanner';

const berlinCalendar = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const parseCalendarDate = (value: string): Date | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = parseISO(value);
  return isValid(date) ? date : null;
};

const getBerlinCalendarDate = (now: Date): Date | null => {
  // Use the Berlin calendar date, regardless of the browser's time zone.
  const parts = berlinCalendar.formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((value) => value.type === type)?.value;
  return parseCalendarDate(`${part('year')}-${part('month')}-${part('day')}`);
};

export const getDepositGuaranteePromoTab = (pathname: string): DepositGuaranteePromoTab | null => {
  const prefix = `${BASE_PATH.replace(/\/+$/, '')}/`;
  if (!pathname.startsWith(prefix)) return null;
  const tab = pathname.slice(prefix.length).split('/')[0];
  return tab === MIETER_PLUS_PATH || tab === WOHNEN_PLUS_PATH ? tab : null;
};

export const hasReachedDepositPromoDay = (entitlements: Entitlement[] | undefined, now = new Date()): boolean => {
  if (!isValid(now)) return false;
  const starts = entitlements
    ?.filter(({ serviceType }) => serviceType === EntitlementType.DOCUMENTS_PLUS_RENT)
    .map(({ serviceStartDate }) => serviceStartDate);
  if (!starts?.length || new Set(starts).size !== 1) return false;

  const startDate = parseCalendarDate(starts[0]);
  if (startDate === null) return false;

  const today = getBerlinCalendarDate(now);
  return today !== null && differenceInCalendarDays(today, startDate) >= 7;
};

export const canPromoteWithLegacyDeposit = (deposit: DepositState | undefined): boolean => {
  if (!deposit) return true;
  // In-progress applications are suppressed as well as existing coverage to avoid duplicate acquisition.
  return [
    DEPOSIT_SERVICE_STATUS.NOT_ACTIVATED,
    DEPOSIT_SERVICE_STATUS.REJECTED,
    DEPOSIT_SERVICE_STATUS.CANCELLED,
  ].includes(deposit.status);
};
